/**
 * État initial, table des coûts et file de résolution — SPEC-MOTEUR §2 et §5.
 *
 * Pureté (CLAUDE.md) : `applyAction` ne mute JAMAIS l'état d'entrée. On clone une fois à
 * l'entrée (`structuredClone`, déterministe) puis on travaille sur le clone.
 */
import { ATELIER_STAGES, InvalidAction, SCHEMA_VERSION_COURANTE, } from "./types.js";
import { H_JOUR, H_NUIT, H_SEMAINE, frontieresDeJour, jour } from "./clock.js";
import { ANNONCE_ENERGIE, ANNONCE_HEURES, pauseDecouverte, plafondMomentum, } from "./pause.js";
import { PUBLICATIONS_LONGUE, argentOuvert, mecaniqueOuverte, publicationsManquantes, } from "./escalier.js";
import { echeanceCarte, purgerCartesPerimees } from "./idees.js";
import { exigerCapacite, validateRangerSerie } from "./bibliotheque.js";
// ⟦M2.10⟧ Le cycle `actions → effects → actions` existe déjà (via `bibliotheque`) et tient :
// les deux bouts sont des `export function`, donc hissées — aucune lecture à l'initialisation.
import { collectModifiers } from "./effects.js";
// ⟦SPEC-FIL §6⟧ La charge utile de `lire_commentaires`, et rien d'autre : le coût de santé
// mentale des commentaires réellement lus (§6.2), la validation du lot (§1.4) et son
// application (§4-§5). `fil.ts` ne connaît pas ce module en retour — la flèche est à sens
// unique, sinon `piocherCartes` et `coutSMTraitements` fermeraient un cycle.
import { CAPACITE_COMMUNITY_MANAGER, capaciteDeLArbre, coutSMTraitements, fil, filActif, traiterFil, validerLotFil, } from "./fil.js";
import { embaucheValide } from "./freelances.js";
import { validateVideoJalon } from "./jalons.js";
import { SYSTEMES_TOUS_ACTIFS, systemeDeLAction, systemes } from "./menage.js";
import { managerActif, managerActive, managerPossede, primeRetourManager } from "./manager.js";
import { consignes as consignesDe, equipeActive, memesConsignes, primeRetourProducteur, producteurActif, producteurPossede, } from "./producteur.js";
import { purgerOpportunites } from "./opportunites.js";
import { progressionRival, rivalDuContenu, rivaux, signalerProgressionRivaux, tirerRivaux } from "./rivaux.js";
import { nouveauRecap } from "./recap.js";
import { SM_DEPASSEMENT_ALERTE, appliquerRepos, effetRepos, heuresDepuisRepos, } from "./repos.js";
import { decroitreSurcharge, grasseMatinee, plafondEnergie, surchargeAccordee, } from "./surcharge.js";
import { appliquerTraitJauge } from "./traits.js";
import { draw, uniformInt } from "./rng.js";
// ─────────────────────────────────────────────────────────────────────────────
// §5 État initial
// ─────────────────────────────────────────────────────────────────────────────
/** Main d'inspiration maximale (PLAN §4.2). */
export const MAIN_MAX = 6;
/** Historique d'alignement retenu pour l'image de chaîne (§7.1). */
export const HISTORIQUE_ALIGNEMENT_MAX = 10;
/** Historique de scores retenu par étape, pour la délégation (§2.2). */
export const HISTORIQUE_ATELIER_MAX = 10;
/**
 * Charges fixes par jour simulé franchi (§2.3 pas 5b, §5.2).
 *
 * **Avenant A-1** : aucune charge n'est débitée avant le Grand Saut (palier 1 000) —
 * « il vit chez ses parents ». Voir `chargesDuJour`.
 */
export const CHARGES_FIXES_PAR_JOUR = 5;
/**
 * Capital de départ versé par l'événement scripté « Le Grand Saut » au franchissement des
 * 1 000 abonnés (**avenant A-1** point 7) : ≈ 4 mois de charges pures.
 * À calibrer par `simulate.ts` (M3.12) — cible : un joueur moyen qui gère ne fait jamais
 * faillite avant ~J250, un négligent peut.
 */
export const CAPITAL_DEPART = 600;
/**
 * Charges dues à une frontière de jour. **Avenant A-1** : 0 avant le Grand Saut.
 * C'est la seule porte des sorties d'argent quotidiennes — la file §2.3 pas 5b l'appelle.
 *
 * ⟦A-11 §2⟧ **Les charges sont un LOYER, donc réversible** : le joueur rentré chez ses parents
 * n'en paie plus. Ce n'est PAS un seuil automatique — A-11 §1 l'interdit explicitement (« si
 * on coupe les charges sous un seuil, on ne pourra jamais faire la fin faillite ») : c'est
 * l'action `retour_parents`, une décision, dont le prix est le studio (`plafondCompetence`).
 */
export function chargesDuJour(state, sys = SYSTEMES_TOUS_ACTIFS) {
    // ⟦C60 · A-177⟧ Le loyer et les charges sont COUPÉS : plus aucune dépense obligatoire. Actif
    // (le défaut), la règle d'avant, au caractère près.
    if (!sys.charges)
        return 0;
    if (!state.flags.grand_saut_done)
        return 0;
    if (state.flags.chez_les_parents === true)
        return 0;
    return CHARGES_FIXES_PAR_JOUR;
}
// ─────────────────────────────────────────────────────────────────────────────
// ⟦A-11 §2⟧ Le retour chez les parents — la porte de sortie, et son prix
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Heures de base d'un déménagement (doublées par `MULT_HEURES_A9` ⇒ **16 h**, soit le budget
 * de travail entier d'une journée) ✎. Déménager n'est pas un clic : c'est la journée qu'on n'a
 * pas passée à produire.
 *
 * ⚠️ ⟦A-19, A-21⟧ **16 h n'est plus « un jour simulé »** — le calendrier en compte 24 depuis
 * qu'on y a mis la nuit. C'est bien `H_EVEILLE_PAR_JOUR` que ce coût épuise, pas `H_JOUR` :
 * un déménagement ne contient donc **aucune nuit** (`nuitsContenues(16) = 0`) et laisse le
 * joueur debout — ce qui est exactement ce qu'on veut raconter.
 */
export const DEMENAGEMENT_HEURES = 8; // ✎
/** Énergie d'un déménagement ✎ — des cartons, un escalier, pas de studio à l'arrivée. */
export const DEMENAGEMENT_ENERGIE = -20; // ✎
/**
 * Prix de **re-partir** de chez ses parents ⟦A-11 §2⟧ : « on ne redéménage pas gratuitement ».
 *
 * ✎ **300 ₵**, et le raisonnement tient en une ligne : à 5 ₵/jour de charges économisées, il
 * faut **60 jours simulés chez ses parents** pour seulement rembourser le retour. Le yo-yo
 * (rentrer, encaisser, repartir) est donc perdant par construction, sans le moindre garde-fou
 * artificiel — exactement ce qu'A-11 §2 demande (« la mécanique s'auto-équilibre »).
 *
 * L'échelle est celle du Grand Saut : `CAPITAL_DEPART` vaut 600 ₵, soit deux déménagements.
 * Le capital que les parents avancent paie littéralement le départ, puis un retour possible.
 */
export const COUT_REPRENDRE_STUDIO = 300; // ✎
// ─────────────────────────────────────────────────────────────────────────────
// ⟦A-11 §3⟧ La production value — l'argent achète du POTENTIEL, jamais du score
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Palier à partir duquel on peut payer une production ⟦A-11 §3⟧ : **100 000 abonnés**.
 *
 * Ces deux constantes vivent ici, et non dans `progress.ts` où le plafond est calculé, parce
 * que `progress.ts` importe déjà `actions.ts` (`CAPITAL_DEPART`, `CHARGES_FIXES_PAR_JOUR`) et
 * que l'inverse fabriquerait un cycle. Même précédent, même sens de flèche.
 */
export const PALIER_PRODUCTION_VALUE = 100_000;
/**
 * Prix qui achète **tout** le bonus de plafond ✎ — au-delà, l'argent n'achète plus rien et
 * l'action est refusée plutôt que de débiter dans le vide (`production_value_invalide`).
 *
 * ✎ **500 ₵**, calé sur ce qu'une longue rapporte à ce palier : `RPM_LONGUE = 4,5 ₵/1 000
 * vues`, donc une vidéo à ~50 000 vues rend ~225 ₵. Le plein tarif coûte **deux bonnes
 * vidéos** : assez cher pour être un arbitrage à chaque production, jamais assez pour être
 * hors de portée d'une chaîne qui a compris que les longues monétisent (⟦A-11 §1⟧).
 */
export const PRODUCTION_VALUE_PRIX_PLEIN = 500; // ✎
/** Bandes d'alignement — arbitrage **A23** : ±30 (CONTENU), pas ±34 (MOTEUR §7.1). */
export const SEUIL_CHAOS = -30;
export const SEUIL_LUMIERE = 30;
export function bandeAlignement(alignement) {
    if (alignement <= SEUIL_CHAOS)
        return "chaos";
    if (alignement >= SEUIL_LUMIERE)
        return "lumiere";
    return "neutre";
}
function historiqueAtelierVide() {
    const h = {};
    for (const stage of ATELIER_STAGES)
        h[stage] = [];
    return h;
}
/** Slot de tendance vide — rempli par `bootstrapRun` puis par la rotation (§8). */
function slotVide() {
    return { trend_id: "", themes: [], alignment: 0, phase: "morte", phase_elapsed_h: 0 };
}
/**
 * État initial d'une run — SPEC-MOTEUR §5 : Abonnés 0, Énergie 100, SM 90, REP 50,
 * Fidélité 50, Momentum 1,0, image 0, 3 cartes d'inspiration.
 *
 * **Argent = 0 et couche argent inactive** (avenant A-1 « Le Grand Saut ») : la spec figée
 * disait 300 ₵, la décision du 13/08 les remplace par 0 ₵ + `CAPITAL_DEPART` versé au
 * palier 1 000.
 *
 * Les 3 cartes et les 3 slots de tendance dépendent du contenu : ils sont posés par
 * `bootstrapRun` (le moteur ne lit aucun fichier). `initialState(mode)` seul rend donc un
 * état structurellement valide mais sans monde — c'est `bootstrapRun` qui le complète, et
 * la création de run côté serveur enchaîne les deux.
 */
export function initialState(mode, genre = null) {
    return {
        // ⟦SPEC-FIL §9.6 · M3.21⟧ **2 depuis que le Fil est branché** — voir `SchemaVersion`.
        schema_version: SCHEMA_VERSION_COURANTE,
        mode,
        // ⟦A-4 §2⟧ Par défaut `null` : AUCUN trait. Voir `GenreAvatar` — un défaut « h » ou « f »
        // appliquerait silencieusement un ×1,2 aux ancres de calibrage.
        genre,
        status: "active",
        ending: null,
        game_over: null,
        victory_locked: false,
        clock_h: 0,
        turn: 0,
        nights: 0,
        subscribers: 0,
        subs_volatile: 0,
        energy: 100,
        mental: 90,
        reputation: 50,
        loyalty: 50,
        // Avenant A-1 : la jauge Argent démarre à 0 et reste INACTIVE jusqu'au Grand Saut
        // (palier 1 000). Le capital de départ arrive avec l'événement scripté (CAPITAL_DEPART).
        money: 0,
        momentum: 1.0,
        inspiration: [],
        alignment_history: [],
        channel_image: 0,
        xp: 0,
        level: 1,
        pc: 0,
        skills: [],
        milestones_reached: [],
        jalons_done: [],
        atelier_history: historiqueAtelierVide(),
        videos: [],
        series: [],
        trends: [slotVide(), slotVide(), slotVide()],
        pending_event: null,
        last_damage: null,
        counters: {
            consecutive_shorts: 0,
            consecutive_flops: 0,
            consecutive_same_theme: 0,
            last_theme: null,
            theme_cooldowns: {},
            last_publication_h: null,
            last_sleep_h: 0,
            sm_crisis_history_h: [],
            strikes: [],
            money_negative_since_h: null,
            subs_at_last_sleep: 0,
            last_milestone_h: 0,
            videos_published: 0,
            respec_done: 0,
        },
        flags: {
            dernier_avertissement_burnout: false,
            arc_chute_active: false,
            sponsor_derniere_chance_used: false,
            apres_la_rue: false,
            accessibility_tolerance: false,
            window_boost_expires_h: null,
            grand_saut_done: false, // avenant A-1
        },
    };
}
/**
 * Complète un état initial avec le monde issu du contenu : les 3 slots de tendance et les
 * 3 cartes d'inspiration de départ (§5). Déterministe : mêmes (contenu, seed) ⇒ même monde.
 * Ne mute pas l'entrée.
 */
export function bootstrapRun(state, content, seed) {
    const next = structuredClone(state);
    for (let slot = 0; slot < 3; slot++) {
        const dejaActives = next.trends.map((t) => t.trend_id).filter((id) => id !== "");
        const candidats = content.trends.filter((t) => !dejaActives.includes(t.id));
        if (candidats.length === 0)
            break;
        const u = draw(seed, next.turn, "trend_rotation", slot);
        const choisie = candidats[uniformInt(u, 0, candidats.length - 1)];
        next.trends[slot] = {
            trend_id: choisie.id,
            themes: [...choisie.themes],
            alignment: choisie.alignement,
            phase: "emergente",
            phase_elapsed_h: 0,
        };
    }
    piocherCartes(next, content, seed, 3);
    // ⟦A-15 §3 / M3.16⟧ Les 2-3 créateurs PNJ de la run — « tirés au bootstrap », déterministes.
    // Vivier absent (`content/rivaux.json` non écrit) ⇒ `state.rivaux` reste vide : l'état neutre.
    tirerRivaux(next, content, seed);
    return next;
}
// ─────────────────────────────────────────────────────────────────────────────
// Inspiration
// ─────────────────────────────────────────────────────────────────────────────
/** Un thème est épuisé jusqu'à `theme_cooldowns[theme]` heures éveillées (§2.4). */
export function themeEpuise(state, theme) {
    const fin = state.counters.theme_cooldowns[theme];
    return fin !== undefined && state.clock_h < fin;
}
/**
 * Pioche `count` cartes d'inspiration (purpose `inspiration_draw`, index = rang du tirage).
 * Exclut les sujets déjà en main et les thèmes épuisés. MUTE `state` — les appelants publics
 * clonent d'abord. S'arrête si la main est pleine (§2.2 : main ≤ 6).
 *
 * Exporté pour `resolve` (M1.17) : certains choix d'événement demandent une carte
 * (`cards_to_draw`), et `events.ts` ne peut pas la tirer lui-même — `inspiration_draw` n'est
 * pas dans sa liste de purposes.
 */
export function piocherCartes(state, content, seed, count, 
/**
 * ⟦SPEC-FIL §5.1⟧ Décalage de l'`index` du tirage `inspiration_draw`. Défaut **0** — donc
 * strictement le comportement d'avant le Fil, au tirage près, pour tous les appelants
 * existants. Un cadeau `idee` tire à `INDEX_CADEAU_IDEE + rang` pour ne jamais collisionner
 * avec la carte de base du même tour ni avec la veille : deux cartes tirées au même index
 * seraient la même carte.
 */
indexBase = 0) {
    let piochees = 0;
    for (let i = 0; i < count; i++) {
        if (state.inspiration.length >= MAIN_MAX)
            break;
        const enMain = new Set(state.inspiration.map((c) => c.subject_id));
        const candidats = content.subjects.filter((s) => !enMain.has(s.id) && !themeEpuise(state, s.theme));
        if (candidats.length === 0)
            break;
        const u = draw(seed, state.turn, "inspiration_draw", indexBase + i);
        const sujet = candidats[uniformInt(u, 0, candidats.length - 1)];
        // ⟦A-15 §1⟧ L'échéance est stampée au TIRAGE, jamais recalculée : une idée sait quand elle
        // se périme, et `echeanceCarte` rend `null` tant que la veille n'est pas ouverte (garde
        // anti-soft-lock — voir `idees.ts`).
        const carte = {
            subject_id: sujet.id,
            drawn_at_h: state.clock_h,
            expires_h: echeanceCarte(state),
        };
        state.inspiration.push(carte);
        piochees++;
    }
    return piochees;
}
function sujet(content, id) {
    return content.subjects.find((s) => s.id === id);
}
// ─────────────────────────────────────────────────────────────────────────────
// §2.2 Table des coûts de CHAQUE action
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Coût d'une action. Signes explicites : `energy_delta` négatif = dépense, positif = gain.
 * `cards_consumed` = cartes défaussées, `cards_drawn` = cartes piochées.
 */
/**
 * ⟦A-66⟧ La vue élargie que `collectModifiers` attend — `EngineContent.skills` et `.boutique`
 * sont optionnels, `ContentBundle` ne l'est pas. Recopiée du `case "produce_video"`, qui la
 * fabriquait déjà en ligne : trois appelants valent une fonction.
 */
function vueEffets(content) {
    return {
        skills: (content.skills ?? []),
        boutique: (content.boutique ?? []),
    };
}
const COUT_NUL = {
    hours: 0,
    energy_delta: 0,
    money_delta: 0,
    mental_delta: 0,
    cards_consumed: 0,
    cards_drawn: 0,
};
/** Coût de base du respec — arbitrage **A6** : 250 ₵ doublé à chaque respec (250/500/1000…). */
export const RESPEC_COUT_BASE = 250;
/**
 * Coût effectif d'une action (§2.2). `mods` accueille les `action_cost_mult` du contenu
 * (SPEC-CONTENU §2.3) — cumul multiplicatif ; absent = aucun modificateur.
 *
 * ⚠️ **Les heures sont arrondies** après multiplication. `clock_h` compte des heures simulées
 * **entières** (§1, §2.1) et toute la mécanique du temps en dépend : frontières de jour,
 * semaine de 112 h, expirations datées. Un `action_cost_mult` sur `heures` à ×0,75 produirait
 * sinon une horloge fractionnaire qui dériverait sans que personne ne le voie. Sans `mods`,
 * ou avec un multiplicateur neutre, l'arrondi ne change rien : la table §2.2 est entière.
 */
export function actionCost(action, state, mods, 
/**
 * ⟦C60 bis · A-177⟧ Les interrupteurs et prix du ménage (`engine/menage.ts`) : le respec et la
 * thérapie lisent ICI leurs Clicks (et la thérapie son énergie). Absent = `SYSTEMES_TOUS_ACTIFS` =
 * la table d'avant, au caractère près (l'état neutre ⟦A-10⟧). Un appelant qui affiche ou valide
 * un coût DOIT passer celui du contenu : valider un coût que la file ne débitera pas est un
 * mensonge (⟦A-11 §1⟧).
 */
sys = SYSTEMES_TOUS_ACTIFS) {
    const base = actionCostBase(action, state, sys);
    if (!mods)
        return base;
    const m = (r) => mods(r, action.type);
    return {
        hours: Math.round(base.hours * m("heures")),
        energy_delta: base.energy_delta < 0 ? base.energy_delta * m("energie") : base.energy_delta,
        money_delta: base.money_delta < 0 ? base.money_delta * m("argent") : base.money_delta,
        mental_delta: base.mental_delta < 0 ? base.mental_delta * m("sante_mentale") : base.mental_delta,
        cards_consumed: base.cards_consumed,
        cards_drawn: base.cards_drawn,
    };
}
/**
 * ⟦A-9⟧ **La table des heures est doublée.** Ce qui manquait au jeu, ce n'est pas de la
 * difficulté, c'est un **choix** : à 16 h de journée et 3 h le short, on enchaînait cinq
 * shorts sans jamais arbitrer. En doublant les heures, une journée porte deux ou trois
 * décisions, pas cinq automatismes.
 *
 * **Pourquoi doubler les COÛTS et surtout pas raccourcir la journée** : `H_JOUR` est la clé de
 * voûte de TOUTES les durées du moteur (semaine, crise, faillite, strike, demi-vie evergreen).
 * Le raccourcir doublerait **silencieusement le sens** de chacune — une crise de 14 jours en
 * deviendrait une de 28. Le multiplicateur vit donc ici, dans la table, où il ne touche aucune
 * constante de temps ni aucune ancre. *(⟦A-19⟧ a bel et bien bougé `H_JOUR`, de 16 à 24, mais
 * dans l'autre sens et en le déclarant : les durées dérivées ont suivi, les nues ont dérivé —
 * voir ⟦A-21⟧.)*
 *
 * **Pourquoi les heures seulement, et pas l'énergie.** L'énergie ne peut pas suivre : une
 * evergreen coûte 68 ⚡, le double ferait 136 ⚡ pour une jauge **bornée à 100** — le format
 * deviendrait littéralement injouable, à tout moment de la partie. Le « produire doit coûter
 * plus d'énergie » de l'arc 1 d'A-9 est un ✎ distinct, à poser avec sa propre valeur.
 *
 * ⚠️ `pause` **n'est pas doublée** : ses heures ne sont pas un coût d'action, c'est
 * `H_SEMAINE` — « la pause dure une semaine ». La doubler la ferait durer deux semaines,
 * exactement la dérive silencieuse que le paragraphe précédent interdit. C'est le même
 * raisonnement qui fait d'`H_LONGUE` un multiple de `H_JOUR` depuis ⟦A-21⟧.
 */
export const MULT_HEURES_A9 = 2;
/**
 * ⟦A-14 · table validée par Mathieu le 15/08, amendée par ⟦A-21⟧⟧ **Les formats longs prennent
 * leurs VRAIES durées**, comptées **en journées simulées** :
 *
 * | | heures | ce que ça raconte |
 * |---|---|---|
 * | Short | **6 h** (`3 × MULT_HEURES_A9`) | trois dans une journée |
 * | Longue | **`3 × H_JOUR` = 72 h** | écriture, tournage, montage |
 * | Longue evergreen | **`4 × H_JOUR` = 96 h** | les quatre jours du métier |
 * | Vidéo jalon | **`4 × H_JOUR` = 96 h** | le morceau de bravoure |
 *
 * ── ⟦A-21⟧ Pourquoi ces trois-là sont DÉRIVÉES et pas écrites en heures ────────
 *
 * Elles valaient `48` et `64`, avec le commentaire « 3 jours simulés » / « 4 jours simulés ».
 * Ces commentaires étaient **vrais** — quand `H_JOUR` valait 16. Puis ⟦A-19⟧ l'a porté à 24
 * (16 h éveillées + 8 h de nuit) : **les nombres n'ont pas bougé, la journée a grandi autour
 * d'eux**, et une longue ne valait plus que 2 jours, un evergreen 2,67. Personne ne l'a vu
 * parce que la table des coûts était rangée sous « heures absolues, ne pas toucher ».
 *
 * Mathieu tranche : **une longue = 3 jours, une evergreen = 4 jours**, son récit d'⟦A-14⟧ est
 * inchangé. Et la leçon vaut plus que les trois nombres : **une durée qui se raconte en
 * journées s'écrit en journées**. Le jour où `H_JOUR` rebougera, elles suivront.
 *
 * ── Ce qui reste légitimement en heures absolues, et pourquoi ──────────────────
 *
 * `MULT_HEURES_A9` reste la règle des **petites** actions (veille 4 h, commentaires 2 h,
 * ménage 2 h, remaster 16 h, déménagement 16 h, thérapie 8 h). Elles disent « ça prend une
 * matinée », pas « ça prend une fraction de journée » : leur sens **ne dérive pas** quand la
 * journée s'allonge, et c'est même tout l'intérêt du multiplicateur d'⟦A-9⟧ — créer un
 * arbitrage **dans** la journée. Les rendre proportionnelles à `H_JOUR` détruirait ce choix.
 *
 * ⚠️ **Aucune mécanique de répartition n'est écrite** : le moteur sait déjà traverser trois
 * frontières de jour d'un seul `clock_h += 72` — les courbes versent, les charges tombent, les
 * tendances avancent. Ce qu'il fallait ajouter n'est pas un ordonnanceur, c'est le **repos
 * contenu** (`engine/repos.ts`), sans quoi ces 72 h compteraient comme une nuit blanche de
 * trois jours. Effet direct et voulu d'⟦A-21⟧ : `nuitsContenues(H_LONGUE)` passe de 2 à **3**
 * et `nuitsContenues(H_LONGUE_EVERGREEN)` de 2 à **4** — le format long cesse d'être une nuit
 * blanche déguisée.
 */
export const H_SHORT = 3 * MULT_HEURES_A9; // 6 h
export const H_LONGUE = 3 * H_JOUR; // 72 h — 3 jours simulés ⟦A-21⟧
export const H_LONGUE_EVERGREEN = 4 * H_JOUR; // 96 h — 4 jours simulés ⟦A-21⟧
export const H_VIDEO_JALON = 4 * H_JOUR; // 96 h — 4 jours simulés ⟦A-21⟧
/**
 * Santé mentale rendue par une nuit (§2.4 pas 1) — ✎ **relevé de 3 à 10** sur décision de
 * Mathieu, pour ouvrir une sortie à la **spirale de fatigue**.
 *
 * **La spirale, mesurée** (audit des formules, B2) : SM < 60 ⇒ Qualité ×0,9 ⇒ la vidéo
 * repasse sous le seuil de nulle ⇒ 17,7 % de flop contre 8,6 % en zone verte ⇒ −5 SM
 * (−8 au deuxième flop consécutif) ⇒ on redescend. **11 236 vidéos sur 12 555 étaient
 * publiées en Fatigue** : ce n'était pas un accident, c'était l'état d'équilibre du jeu. Et
 * la sortie était **arithmétiquement fermée** — il aurait fallu 95,6 de score quand le
 * plafond de compétence sature à 85.
 *
 * **Pourquoi le sommeil, et pas la thérapie.** La thérapie coûte 150 ₵, donc elle n'existe
 * pas avant le Grand Saut : pendant tout l'arc 1, le joueur n'avait **aucun** remède. Dormir
 * est la seule action toujours disponible et gratuite — et c'est thématiquement juste : un
 * débutant épuisé ne va pas en thérapie, il dort. C'est aussi ce qui referme la boucle
 * qu'⟦A-9⟧ ouvre de l'autre côté (« le coût de SM vient du dépassement d'heures ») : on perd
 * de la santé mentale en travaillant trop, on la regagne en se reposant.
 *
 * **Mesuré** — un joueur qui démarre à SM 45 et joue 40 jours en dormant quand il est bas :
 *
 * | `SM_NUIT` | SM finale (méd.) | plancher | runs qui remontent | publications en Fatigue |
 * |---|---|---|---|---|
 * | 3 (avant) | **32** | 3 | **0/12** | **100 %** |
 * | 5 | 40 | 25 | 2/12 | 98,2 % |
 * | 8 | 80 | 40 | 10/12 | 54,3 % |
 * | **10** | **97** | 40 | **12/12** | **33,8 %** |
 *
 * La zone de Fatigue **existe toujours** — un tiers des publications s'y font encore pendant
 * la remontée, et le plancher reste à 40. Ce qui change, c'est qu'on en SORT.
 *
 * ⚠️ **Le ×0,9 de Fatigue et le seuil de 60 ne bougent PAS** : ce sont eux qui font exister
 * la fatigue comme enjeu. On voulait une sortie, pas une suppression.
 */
export const SM_NUIT = 10; // ✎
/**
 * ⟦A-4 §2 / A-26⟧ Le trait de SM du créateur, posé sur la table §2.2 elle-même.
 *
 * **Pourquoi ici et pas au débit** : ⟦A-11 §1⟧ exige que le coût prévisualisé soit exactement
 * celui qui sera débité — le client affiche `actionCost` avant le clic. Un trait posé plus
 * loin dans la file ferait mentir cet affichage. (C'était déjà l'argument du transitoire
 * `coutCommentaires`, retiré en M3.21 : voir la table §2.2, `lire_commentaires`.)
 *
 * **Pourquoi un enrobage plutôt que deux appels dans le `switch`** : la règle est « toute
 * remontée de SM hors sommeil », pas « thérapie et pause ». Écrite au cas par cas, elle
 * serait oubliée par la prochaine action qui rend de la SM ; écrite ici, elle est acquise.
 *
 * `sleep` est la seule exception, et c'est le mot de l'avenant : **le sommeil est intact des
 * deux côtés**. `appliquerTraitJauge` laisse par ailleurs passer tous les deltas ≤ 0 : les
 * COÛTS de SM ne sont jamais touchés (règle 1 de `traits.ts`), seulement les gains.
 */
function actionCostBase(action, state, sys) {
    const base = actionCostTable(action, state, sys);
    const mental = appliquerTraitJauge(state.genre, "mental", base.mental_delta, {
        hors_sommeil: action.type !== "sleep",
    });
    return mental === base.mental_delta ? base : { ...base, mental_delta: mental };
}
/** Un prix signé pour la table des coûts : `0` reste `0` (jamais un `-0` dans le récap ni à l'écran). */
function moins(prix) {
    return prix > 0 ? -prix : 0;
}
function actionCostTable(action, state, sys) {
    const h = (heures) => heures * MULT_HEURES_A9;
    switch (action.type) {
        case "produce_video": {
            // ⟦A-11 §3⟧ La production value est une DÉPENSE de l'action : elle est débitée par la
            // table, comme tout le reste. Absente ⇒ 0 ⇒ aucune ancre ne bouge.
            const pv = -(action.production_value ?? 0);
            if (action.format === "short") {
                return { ...COUT_NUL, hours: H_SHORT, energy_delta: -12, money_delta: pv, cards_consumed: 1 };
            }
            // ⟦A-14 table du 15/08⟧ Durées réelles : 3 jours la longue, 4 l'evergreen. L'énergie et
            // les cartes ne bougent pas — c'est le TEMPS qui devient vrai, pas le coût.
            return action.evergreen_intent
                ? { ...COUT_NUL, hours: H_LONGUE_EVERGREEN, energy_delta: -68, money_delta: pv, cards_consumed: 3 }
                : { ...COUT_NUL, hours: H_LONGUE, energy_delta: -45, money_delta: pv, cards_consumed: 2 };
        }
        case "video_jalon":
            return { ...COUT_NUL, hours: H_VIDEO_JALON, energy_delta: -45 };
        case "sleep":
            // ⟦A-19⟧ **La nuit coûte ses heures.** Elle valait 0 h « hors horloge » (§2.1), donc
            // dormir ne franchissait AUCUNE frontière de jour : ni rente, ni désabonnements, ni
            // decay de momentum, ni péremption d'idées, ni progression du rival — au moment précis
            // où le joueur attend que le monde tourne. SM : voir `SM_NUIT`.
            return { ...COUT_NUL, hours: H_NUIT, energy_delta: 80, mental_delta: SM_NUIT };
        case "veille":
            return { ...COUT_NUL, hours: h(2), energy_delta: -8, cards_drawn: 2 };
        case "lire_commentaires": {
            // ⟦SPEC-FIL §6.2⟧ 🔑 **Le forfait devient un REPLI.** Avec une charge utile et un Fil
            // non vide, le coût de santé mentale est celui des commentaires **réellement traités**,
            // et le forfait ne s'applique PAS en plus : on ne paie pas deux fois. Sans charge utile
            // — ou Fil vide, ou Fil éteint — c'est le forfait d'aujourd'hui, au point près : l'état
            // neutre ⟦A-10⟧, et les trois régimes du §6.3 tiennent dans ce seul `if`.
            //
            // C'est la seule façon d'être honnête. Un joueur qui tombe sur trois messages adorables
            // ne perd rien ; un joueur qui encaisse trois messages haineux paie plus que le forfait.
            // C'est exactement le dilemme que PLAN §4.10 décrit et que le forfait aplatissait.
            const traitements = action.traitements ?? [];
            if (traitements.length > 0 && fil(state).length > 0) {
                return {
                    ...COUT_NUL,
                    hours: h(1),
                    energy_delta: -4,
                    mental_delta: coutSMTraitements(state, traitements),
                    cards_drawn: 1,
                };
            }
            // SM −4, doublé à −8 si image ≤ −40 (« commentaires toxiques », PLAN §4.7).
            //
            // ⟦A-4 §2 · SPEC-FIL §7 · M3.21⟧ 🔴 **Le trait genré n'est plus ici.** Ce coût portait un
            // `coutCommentaires(state.genre, …)` qui le multipliait par 1,5 pour la créatrice, faute
            // de Fil où faire mordre « le Fil est plus rude ». Le Fil est branché : le trait pèse
            // désormais sur le **tirage** (`MULT_TOXIQUES_F`, `fil.ts`), et garder les deux aurait
            // fait payer le trait deux fois — plus de toxiques **et** chacun 50 % plus cher.
            // Le coût d'un commentaire est le même pour tout le monde ; seule sa fréquence diffère.
            return {
                ...COUT_NUL,
                hours: h(1),
                energy_delta: -4,
                mental_delta: state.channel_image <= -40 ? -8 : -4,
                cards_drawn: 1,
            };
        }
        case "menage":
            return { ...COUT_NUL, hours: h(1), energy_delta: -4, mental_delta: -0.5 };
        case "remaster":
            return { ...COUT_NUL, hours: h(8), energy_delta: -27, cards_consumed: 1 };
        case "ranger_serie":
            return { ...COUT_NUL, hours: h(1), energy_delta: -4 };
        case "acheter_skill":
            // PC et Clicks du nœud : débités par M1.14 (ils dépendent du contenu).
            return COUT_NUL;
        case "respec":
            // ⟦C60 bis · A-177⟧ Le prix de base vient du contenu (250 avant, 0 : le respec est gratuit).
            return {
                ...COUT_NUL,
                money_delta: moins(sys.respec_clicks * Math.pow(2, state.counters.respec_done)),
            };
        case "pause":
            // ⚠️ H_SEMAINE, pas un coût d'action : non doublé (voir `MULT_HEURES_A9`).
            return { ...COUT_NUL, hours: H_SEMAINE, energy_delta: 50, mental_delta: 40 };
        case "annoncer_pause":
            // Avenant A-2 point 4 : tournage d'un message à la communauté.
            return { ...COUT_NUL, hours: h(ANNONCE_HEURES), energy_delta: ANNONCE_ENERGIE };
        case "therapie":
            // ⟦C60 bis · A-177⟧ Les Clicks ne paient plus que le Marché et l'Équipe : la séance se paie en
            // TEMPS (les mêmes heures) et en ÉNERGIE, deux réglages du contenu (150 ₵ et 4 d'énergie avant).
            return {
                ...COUT_NUL,
                hours: h(4),
                energy_delta: moins(sys.therapie_energie),
                money_delta: moins(sys.therapie_clicks),
                mental_delta: 15,
            };
        // ⟦A-11 §2⟧ Les deux déménagements. Le retour ne coûte pas de Clicks — on rentre PARCE
        // QU'ON n'en a plus ; son prix est le studio, donc le plafond de compétence. Repartir, si.
        case "retour_parents":
            return {
                ...COUT_NUL,
                hours: h(DEMENAGEMENT_HEURES),
                energy_delta: DEMENAGEMENT_ENERGIE,
            };
        case "reprendre_studio":
            return {
                ...COUT_NUL,
                hours: h(DEMENAGEMENT_HEURES),
                energy_delta: DEMENAGEMENT_ENERGIE,
                money_delta: -COUT_REPRENDRE_STUDIO,
            };
        case "acheter_objet":
            // Prix et effets de l'objet : débités par `boutique.ts` (ils dépendent du contenu),
            // exactement comme `acheter_skill` laisse ses PC et ses Clicks à `skills.ts`.
            return COUT_NUL;
        case "choix_evenement":
        // ⟦A-50 §3 levier 1 · A-108⟧ « Je gère » ne coûte RIEN par elle-même, exactement comme
        // `choix_evenement` : son prix est celui du premier choix de l'événement (l'« issue par
        // défaut »), appliqué par la même résolution — voir `resoudreChoixEvenement` (`events.ts`).
        case "reporter_evenement":
        // ⟦C8 · A-120⟧ Même famille : « le manager tranche » ne coûte rien par elle-même non plus,
        // son prix est celui du choix qu'il joue (`resoudreTrancheParManager`, `events.ts`).
        case "trancher_par_manager":
        case "clore_run":
        case "continuer_apres_rue":
            return COUT_NUL;
        /**
         * ⟦SPEC-PRODUCTEUR §11.1 / A-66⟧ **`laisser_tourner` ne coûte RIEN par elle-même**, et
         * c'est structurel : son coût est **la somme des actions qu'elle joue**, chacune facturée
         * par cette même table quand `resolve` la traite. Lui donner un coût propre le compterait
         * deux fois — et surtout, ça déplacerait l'équilibrage sans qu'on sache où.
         */
        case "laisser_tourner":
            return COUT_NUL;
        /**
         * ⟦SPEC-PRODUCTEUR §3.1 ✎ 8⟧ **La réunion d'équipe : 2 h et −8 énergie.** C'est ce qui
         * empêche les consignes de redevenir un clic par vidéo — se raviser trois fois dans la
         * semaine coûte une demi-journée de travail. ⟦A-15⟧ : le temps doit coûter.
         *
         * ⟦§6.3⟧ **Mais congédier et rappeler sont GRATUITS en temps** (« gratuit, 0 h ✎ ») : la
         * réunion ne se facture que si la LIGNE change. Sans cette distinction, la porte de
         * sortie de Mathieu (*« si tu as viré tout le monde, ça s'enlève »*) coûterait une
         * demi-matinée — une pénalité que la table de §6.3 interdit explicitement. La prime de
         * retour, elle, est débitée par l'effet (`appliquerReglageEquipe`), comme le prix d'un
         * objet de boutique : elle vient du contenu, pas de cette table.
         */
        case "regler_equipe": {
            // ⟦C8 · A-120⟧ Recruter un candidat EST une décision de build, au même titre qu'un
            // changement de consignes : elle se raviser coûte la réunion, même si la ligne
            // éditoriale et le rythme n'ont pas bougé.
            const changeProducteur = action.producteur_candidat_id !== undefined &&
                action.producteur_candidat_id !== state.flags.producteur_candidat_id;
            const changeManager = action.manager_candidat_id !== undefined &&
                action.manager_candidat_id !== state.flags.manager_candidat_id;
            return memesConsignes(consignesDe(state), action.consignes) && !changeProducteur && !changeManager
                ? COUT_NUL
                : { ...COUT_NUL, hours: h(2), energy_delta: -8 };
        }
        // ⟦SPEC-FIL §9.5 · A-82⟧ Arrêter ou rappeler le community manager : gratuit, 0 h — la porte
        // de sortie d'une délégation n'est jamais un piège ⟦SPEC-PRODUCTEUR §6.3⟧.
        case "regler_cm":
            return COUT_NUL;
    }
}
/** Coût énergétique d'une re-take de Tournage — arbitrage **A3** : 2 ⚡, 0 h, max 2/prise. */
export const RETAKE_ENERGIE = 2;
export const RETAKE_MAX_PAR_PRISE = 2;
/** Crunch : énergie < 20 au lancement d'une production (§2.2) ⇒ Qualité ×0,7 et SM −5. */
export const SEUIL_CRUNCH = 20;
export const CRUNCH_SM = -5;
// ─────────────────────────────────────────────────────────────────────────────
// §2.2 Validations — une action invalide ne consomme ni turn ni tirage
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Valide une action (§2.2). Lève `InvalidAction` sans le moindre effet de bord.
 * Toutes les validations qui dépendent de collections de contenu pas encore branchées
 * (skills en M1.14, jalons en M3.1, événements en M1.15) sont marquées et arriveront avec
 * leur tâche — elles ne sont pas silencieusement permissives : leur absence est tracée ici.
 */
export function validateAction(state, action, content, mods, 
/**
 * ⟦M2.10⟧ La graine de la run. Elle n'est nécessaire qu'à **une** validation : l'offre du
 * marché des freelances est dérivée de `(seed, fenêtre)`, donc « ce freelance est-il là
 * cette semaine ? » et « ai-je de quoi le payer ? » ne se répondent pas sans elle.
 *
 * Facultative pour ne pas casser les appelants qui valident hors résolution (tests, garde
 * de l'API). Absente, seule la part **indépendante du marché** est vérifiée — la capacité,
 * c'est-à-dire le refus qui compte pour 99 % des cas. `applyAction`, seul chemin par lequel
 * une action est réellement jouée, la fournit toujours : rien ne passe en production sans
 * le contrôle complet.
 */
seed) {
    // Run terminée : plus rien ne s'y joue (§1.6). `ended` compte aussi — c'est la run close
    // par `clore_run` après le million (PLAN §4.13) : le mémorial est figé, on n'y rejoue pas.
    if (state.status === "game_over" || state.status === "ended") {
        throw new InvalidAction("run_not_active");
    }
    // ⟦C60 · A-177⟧ **Un système coupé n'a plus d'action.** Les jalons, la boutique, le logement et la
    // gestion de la bibliothèque sont fermés par `content/meta.json → systemes` : un appel direct à
    // l'API n'ouvre pas ce que le contenu a fermé (le moteur est autoritatif, PLAN §6). Même code
    // que les autres mécaniques verrouillées : le client sait déjà l'afficher. Systèmes actifs (le
    // défaut), cette ligne ne refuse rien.
    const sys = systemes(content);
    const systemeFerme = systemeDeLAction(action);
    if (systemeFerme !== null && !sys[systemeFerme]) {
        throw new InvalidAction("mecanique_verrouillee", { mecanique: systemeFerme, coupe: true });
    }
    // Un pending_event bloque TOUTE action sauf sa résolution — ⟦A-50 §3 levier 1 · A-108⟧ « Je
    // gère » (`reporter_evenement`) EST une résolution, juste pas celle du joueur (§2.3, A24).
    // ⟦C8 · A-120⟧ « Le manager tranche » (`trancher_par_manager`) en est une troisième : ni le
    // joueur, ni sa compétence — l'employé, à l'expiration d'un minuteur qui vit côté CLIENT.
    if (state.pending_event &&
        action.type !== "choix_evenement" &&
        action.type !== "reporter_evenement" &&
        action.type !== "trancher_par_manager") {
        throw new InvalidAction("pending_event_blocks", {
            event_id: state.pending_event.event_id,
        });
    }
    // ⟦A-15 §4⟧ Répondre à une **opportunité ouverte** est légitime même sans `pending_event` :
    // c'est la même action pour les deux provenances (voir `GameState.opportunites`). Le détail
    // de la validation — offre inconnue, fenêtre déjà refermée — appartient à `events.ts`, qui
    // seul reçoit le contenu.
    if (action.type === "choix_evenement" &&
        !state.pending_event &&
        !(state.opportunites ?? []).some((o) => o.event_id === action.event_id)) {
        throw new InvalidAction("no_pending_event");
    }
    // ⟦A-50 §3 levier 1 · A-108⟧ « Je gère » ne reporte QU'un `pending_event` — jamais une
    // `opportunite` (déjà ignorable gratuitement, PLAN §4.9 : rien à « gérer » là). Gardée par la
    // capacité `reporter_evenement` (nœud *Gestion de crise*, Charisme) : sans elle, l'action
    // n'existe pas pour le joueur — même refus que `mecanique_verrouillee` ailleurs.
    if (action.type === "reporter_evenement") {
        if (!state.pending_event)
            throw new InvalidAction("no_pending_event");
        if (!capaciteDeLArbre(state, content, "reporter_evenement")) {
            throw new InvalidAction("mecanique_verrouillee", { mecanique: "reporter_evenement" });
        }
    }
    // ⟦C8 · A-120⟧ « Le manager tranche » exige un `pending_event` (même garde que « Je gère »)
    // ET un manager réellement en poste — pas seulement possédé : congédié, ou son producteur
    // absent (§6.3), il ne tranche rien.
    if (action.type === "trancher_par_manager") {
        if (!state.pending_event)
            throw new InvalidAction("no_pending_event");
        const mods = collectModifiers(state, vueEffets(content));
        if (!managerActif(state, content, mods, producteurActif(state, mods))) {
            throw new InvalidAction("manager_absent", { possede: managerPossede(mods) });
        }
    }
    // ⟦A-11 §1⟧ Le coût VALIDÉ est le coût MODIFIÉ : sans `mods`, un joueur dont le matériel
    // divise la dépense par deux se voyait quand même refuser l'action pour « pas assez
    // d'énergie ». Valider un coût que la file ne débitera pas est un mensonge.
    const cout = actionCost(action, state, mods, sys);
    // Validations STRUCTURELLES d'abord, ressources ensuite : c'est l'ordre de la table §2.2
    // (« abonnés ≥ 100 ; énergie ≥ 45 ; 2 cartes en main ») et c'est le plus utile au joueur —
    // « le respec est verrouillé sous 10 000 abonnés » vaut mieux que « tu es fauché », surtout
    // depuis l'avenant A-1 où l'on démarre à 0 ₵.
    // ⟦A-5⟧ L'escalier de déverrouillage passe AVANT tout le reste — y compris avant les
    // ressources. « Tu n'as pas encore ça » est plus utile que « tu es fauché » pour une
    // mécanique que le joueur n'a pas encore ouverte, et c'est l'ordre que §2.2 impose.
    // ⟦A-9⟧ L'arbre rejoint la veille et les commentaires : son étage passe au compteur de
    // publications, donc il y a désormais une porte à refuser. Avant, on comptait sur `pc = 0`
    // pour fermer l'achat — un verrou de ressource, pas un étage.
    if (action.type === "veille" ||
        action.type === "lire_commentaires" ||
        action.type === "acheter_skill") {
        const mecanique = action.type === "acheter_skill" ? "arbre" : action.type;
        if (!mecaniqueOuverte(state, mecanique)) {
            throw new InvalidAction("mecanique_verrouillee", {
                mecanique,
                publications_manquantes: publicationsManquantes(state, mecanique),
                videos_publiees: state.counters.videos_published,
            });
        }
    }
    switch (action.type) {
        case "produce_video": {
            // ⟦A-9⟧ La longue s'ouvre au compteur de PUBLICATIONS, plus aux abonnés. Les abonnés
            // referment la boucle sur elle-même (le plafond écrase la qualité ⇒ moins d'abonnés ⇒
            // le format ne s'ouvre jamais) ; les publications, le débutant les contrôle. Le code
            // d'erreur reste `format_locked` — c'est la même porte, pas la même clé — et les
            // `details` portent les deux lectures pour que l'UI puisse dire ce qu'il manque.
            if (action.format === "longue" && !mecaniqueOuverte(state, "longue")) {
                throw new InvalidAction("format_locked", {
                    videos_publiees: state.counters.videos_published,
                    requis: PUBLICATIONS_LONGUE,
                    publications_manquantes: publicationsManquantes(state, "longue"),
                    subscribers: state.subscribers,
                });
            }
            // ⟦A-11 §3⟧ Production value : refusée hors de son palier ou au-delà du plein tarif.
            // On REFUSE au lieu de plafonner en silence — débiter des Clicks qui n'achètent rien
            // serait la pire des dérives invisibles.
            const pv = action.production_value ?? 0;
            // ⟦C60 bis · A-177⟧ Les Clicks ne paient plus que le Marché et l'Équipe : la production value
            // est COUPÉE (le levier n'existe plus). Même refus que les autres systèmes fermés, et la
            // production value `0` (l'absence) passe, comme avant.
            if (pv !== 0 && !sys.production_value) {
                throw new InvalidAction("mecanique_verrouillee", { mecanique: "production_value", coupe: true });
            }
            if (pv !== 0) {
                const ouverte = state.subscribers >= PALIER_PRODUCTION_VALUE ||
                    state.milestones_reached.includes(PALIER_PRODUCTION_VALUE);
                if (!Number.isFinite(pv) || pv < 0) {
                    throw new InvalidAction("production_value_invalide", { raison: "negative", production_value: pv });
                }
                if (!ouverte) {
                    throw new InvalidAction("production_value_invalide", {
                        raison: "palier",
                        requis: PALIER_PRODUCTION_VALUE,
                        subscribers: state.subscribers,
                    });
                }
                if (pv > PRODUCTION_VALUE_PRIX_PLEIN) {
                    throw new InvalidAction("production_value_invalide", {
                        raison: "plafond",
                        max: PRODUCTION_VALUE_PRIX_PLEIN,
                        production_value: pv,
                    });
                }
            }
            const principal = sujet(content, action.subject_id);
            if (!principal)
                throw new InvalidAction("subject_not_in_hand", { subject_id: action.subject_id });
            if (!state.inspiration.some((c) => c.subject_id === action.subject_id)) {
                throw new InvalidAction("subject_not_in_hand", { subject_id: action.subject_id });
            }
            if (themeEpuise(state, principal.theme)) {
                throw new InvalidAction("theme_exhausted", {
                    theme: principal.theme,
                    jusqua_h: state.counters.theme_cooldowns[principal.theme] ?? null,
                });
            }
            // Cartes supplémentaires de la longue (§2.2, choix §14.6) : elles doivent être en main
            // et distinctes du sujet principal.
            if (action.format === "longue") {
                const requis = cout.cards_consumed - 1;
                // ⚠️ `?? []` : `extra_card_subjects` est obligatoire dans le type, mais une action
                // arrive du RÉSEAU (SPEC-API §7.1). Sans ce garde-fou, une longue sans le champ levait
                // un `TypeError` brut — donc un 500 côté serveur, au lieu de l'`InvalidAction`
                // `not_enough_cards` que le contrat promet et que le client sait afficher. La règle du
                // §2.3 est qu'une action invalide se REFUSE, elle ne casse pas le moteur.
                const fournies = action.extra_card_subjects ?? [];
                const extras = new Set(fournies.filter((id) => id !== action.subject_id));
                for (const id of extras) {
                    if (!state.inspiration.some((c) => c.subject_id === id)) {
                        throw new InvalidAction("not_enough_cards", { manquante: id });
                    }
                }
                if (extras.size < requis) {
                    throw new InvalidAction("not_enough_cards", { requis, fournies: extras.size });
                }
            }
            // ⟦A-4 E / M2.10⟧ **Le marché des freelances.** C'est la validation que `publish.ts`
            // annonçait depuis M1 sans que personne ne la câble (« M2.10 la câble dans
            // validateAction avec le code `delegation_locked` ») — et sans elle, une étape
            // déléguée sans droit passait en silence.
            if (action.scores.delegated.length > 0) {
                // ⟦A-29⟧ Le marché est ce qui coûte de l'argent : avant le Grand Saut, il n'existe
                // pas. Le refus générique de `mecanique_verrouillee` ne peut pas l'attraper — le prix
                // d'un freelance ne passe pas par la table des coûts (même cas que `cout_euros` d'un
                // nœud, cf. le « garde jumeau » d'A-29).
                if (!argentOuvert(state)) {
                    throw new InvalidAction("mecanique_verrouillee", {
                        mecanique: "argent",
                        etapes: action.scores.delegated.join(","),
                    });
                }
                const verdict = embaucheValide(action.scores, collectModifiers(state, {
                    skills: (content.skills ?? []),
                    boutique: (content.boutique ?? []),
                }), state, content, seed ?? "", action.format === "short" ? "short" : "longue");
                if (!verdict.ok && (seed !== undefined || verdict.raison === "capacite")) {
                    // ⟦SPEC-PRODUCTEUR §1.5⟧ **Deux codes, deux réalités.** `delegation_locked` dit
                    // « le droit manque » (capacité, personne au marché) ; `delegation_impayable` dit
                    // « le droit existe, la caisse ne suit pas » — le code que la spec promettait et
                    // que le recompte du 20/08 a trouvé remontant déguisé.
                    throw new InvalidAction(verdict.raison === "argent" ? "delegation_impayable" : "delegation_locked", {
                        raison: verdict.raison,
                        stage: verdict.stage,
                        ...(verdict.details ?? {}),
                    });
                }
            }
            break;
        }
        case "lire_commentaires":
            if (state.inspiration.length >= MAIN_MAX) {
                throw new InvalidAction("not_enough_cards", { main: state.inspiration.length, max: MAIN_MAX });
            }
            // ⟦SPEC-FIL §1.4⟧ Le lot est vérifié AVANT tout effet et avant tout tirage : lot trop
            // grand, `uid` inconnu ou répété, réponse que le contenu n'autorise pas. Comme toute
            // action invalide — aucun effet, aucun turn, aucun tirage (§2.3 pas 1).
            if (action.traitements !== undefined && action.traitements.length > 0) {
                validerLotFil(state, content, action.traitements);
            }
            break;
        case "menage": {
            // ⟦C13⟧ Même correctif que `ranger_serie` : `menage_bibliotheque` était vérifiée par le
            //   client et par personne d'autre.
            exigerCapacite(state, content, "menage_bibliotheque");
            const v = state.videos.find((x) => x.id === action.video_id);
            if (!v)
                throw new InvalidAction("video_not_found", { video_id: action.video_id });
            if (v.class !== "nulle" || v.visibility !== "public") {
                throw new InvalidAction("video_wrong_class", { video_id: action.video_id, classe: v.class });
            }
            break;
        }
        case "remaster": {
            const v = state.videos.find((x) => x.id === action.video_id);
            if (!v)
                throw new InvalidAction("video_not_found", { video_id: action.video_id });
            if (v.class !== "evergreen" || !v.evergreen) {
                throw new InvalidAction("video_wrong_class", { video_id: action.video_id, classe: v.class });
            }
            break;
        }
        // ⟦C13⟧ La capacité et la compatibilité de thème étaient vérifiées **côté client
        //   seulement** : un appel direct à l'API contournait l'arbre de skills. Le moteur est
        //   autoritatif (PLAN §6), donc le refus vit ici. Toute la règle est dans
        //   `bibliotheque.ts`, y compris le ≥ 3 — la dupliquer ici la ferait dériver.
        case "ranger_serie":
            validateRangerSerie(state, action.video_ids, content);
            break;
        // §9.3 / M3.1 — Le jalon est validé par le CONTENU (palier, nœuds, flags, image) et par
        //   son propre cooldown. Sans ce `case`, l'action tombait dans le `default:` : 64 h et
        //   45 ⚡ facturés, crunch encaissé, tirage d'événement subi, et aucun jalon joué.
        case "video_jalon":
            validateVideoJalon(state, action.jalon_id, content);
            // ⟦M2.10⟧ **Un jalon ne se délègue pas.** C'est le morceau de bravoure de la chaîne
            // (§2.2) — et surtout, `index.ts` fait passer ses scores par `scoresEffectifs` SANS
            // contexte d'embauche : un `delegated` accepté ici serait ni substitué, ni facturé,
            // c'est-à-dire un **no-op silencieux**. On refuse au lieu d'ignorer.
            if (action.scores.delegated.length > 0) {
                throw new InvalidAction("delegation_locked", {
                    raison: "jalon",
                    jalon_id: action.jalon_id,
                    etapes: action.scores.delegated.join(","),
                });
            }
            break;
        case "respec":
            if (state.subscribers < 10_000) {
                throw new InvalidAction("respec_locked", { subscribers: state.subscribers, requis: 10_000 });
            }
            break;
        case "annoncer_pause":
            // Avenant A-2 : semi-cachée. Elle se révèle en jouant (1er passage SM < 30) ou par le
            // nœud dédié — tant qu'elle ne l'est pas, elle n'existe pas pour le joueur.
            // ⟦A-82⟧ `content` : la route « par l'arbre » (capacité *Cœur sur vous*) se lit dedans.
            if (!pauseDecouverte(state, content))
                throw new InvalidAction("pause_non_decouverte");
            if (state.pause_announced) {
                throw new InvalidAction("pause_non_decouverte", { deja_en_pause: true });
            }
            break;
        // ⟦A-11 §2⟧ Les deux sens du déménagement. Une seule règle, deux lectures : on ne peut
        // rentrer que si l'on est parti, on ne peut repartir que si l'on est rentré. **Rien
        // d'automatique** — A-11 §1 : la faillite doit rester atteignable pour qui ne réagit pas.
        case "retour_parents":
            if (!state.flags.grand_saut_done) {
                // Avant le Grand Saut, il y vit déjà (⟦A-1⟧) : il n'y a rien à quitter.
                throw new InvalidAction("logement_invalide", {
                    raison: "pas_encore_parti",
                    grand_saut_done: false,
                });
            }
            if (state.flags.chez_les_parents === true) {
                throw new InvalidAction("logement_invalide", { raison: "deja_chez_les_parents" });
            }
            break;
        case "reprendre_studio":
            if (state.flags.chez_les_parents !== true) {
                throw new InvalidAction("logement_invalide", { raison: "pas_chez_les_parents" });
            }
            break;
        /**
         * ⟦SPEC-PRODUCTEUR §5.2 / A-66⟧ Les trois refus de la délégation. `pending_event_blocks`
         * est déjà posé par la garde générale au-dessus : un événement en attente bloque TOUT.
         */
        case "laisser_tourner":
            if (!equipeActive(content)) {
                throw new InvalidAction("mecanique_verrouillee", { mecanique: "equipe" });
            }
            {
                const mods = collectModifiers(state, vueEffets(content));
                if (!producteurActif(state, mods)) {
                    throw new InvalidAction("producteur_absent", {
                        possede: producteurPossede(mods),
                        actif: state.flags.producteur_actif !== false,
                    });
                }
            }
            break;
        case "regler_cm":
            // ⟦SPEC-FIL §9.5 · A-82⟧ Il faut un Fil allumé et quelqu'un à régler : la capacité
            // `community_manager`, portée par un nœud de l'arbre (*Le Studio*). Refusé ICI, avant tout
            // effet — une action invalide ne consomme ni turn ni tirage (§2.3).
            if (!filActif(content)) {
                throw new InvalidAction("mecanique_verrouillee", { mecanique: "fil" });
            }
            if (!capaciteDeLArbre(state, content, CAPACITE_COMMUNITY_MANAGER)) {
                throw new InvalidAction("mecanique_verrouillee", { mecanique: CAPACITE_COMMUNITY_MANAGER });
            }
            break;
        case "regler_equipe":
            if (!equipeActive(content)) {
                throw new InvalidAction("mecanique_verrouillee", { mecanique: "equipe" });
            }
            // Régler la ligne suppose qu'il y ait quelqu'un à qui la donner — mais **pas** qu'il
            // soit actif : c'est par cette action qu'on le réembauche (§6.3).
            if (!producteurPossede(collectModifiers(state, vueEffets(content)))) {
                throw new InvalidAction("producteur_absent", { possede: false, actif: false });
            }
            // ⟦C8 · A-120⟧ Recruter (ou changer) un candidat : il doit exister dans le vivier de
            // contenu, sinon `candidat_inconnu` — jamais un no-op silencieux sur un id qui n'existe
            // pas. Vérifié AVANT les primes ci-dessous : elles lisent ce même candidat.
            if (action.producteur_candidat_id !== undefined) {
                const connu = (content.producteur?.candidats_producteur ?? []).some((c) => c.id === action.producteur_candidat_id);
                if (!connu) {
                    throw new InvalidAction("candidat_inconnu", { id: action.producteur_candidat_id, role: "producteur" });
                }
            }
            if (action.manager_candidat_id !== undefined) {
                // Même garde que `manager_actif` ci-dessous : recruter suppose l'étage possédé.
                if (!managerActive(content) || !managerPossede(collectModifiers(state, vueEffets(content)))) {
                    throw new InvalidAction("mecanique_verrouillee", { mecanique: "manager" });
                }
                const connu = (content.managers ?? []).some((m) => m.id === action.manager_candidat_id);
                if (!connu) {
                    throw new InvalidAction("candidat_inconnu", { id: action.manager_candidat_id, role: "manager" });
                }
            }
            // ⟦§6.3⟧ Rappeler se paie : `PRIME_RETOUR`, sinon `not_enough_money` — refusé ICI, avant
            // tout effet, parce qu'une action invalide ne consomme ni turn ni tirage (§2.3). L'écran
            // annonce ce prix depuis A-66 ; c'est ce refus-là qui l'empêche de mentir.
            if (action.producteur_actif && state.flags.producteur_actif === false) {
                // ⟦C8 · A-120⟧ Le candidat visé par CETTE action, s'il en recrute un — la prime doit
                // être celle de la recrue, pas celle de l'ancienne titulaire.
                const prime = primeRetourProducteur(state, content, action.producteur_candidat_id);
                if (state.money < prime) {
                    throw new InvalidAction("not_enough_money", {
                        requis: prime,
                        solde: Math.round(state.money),
                        raison: "prime_retour",
                    });
                }
            }
            // ⟦§1.3 / §6.3 · M3.29⟧ **Toucher au manager exige de l'avoir.** Le champ absent ne
            // touche à rien ; posé sans le nœud (ou interrupteur éteint — la capacité est filtrée),
            // c'est un refus franc, jamais un no-op silencieux.
            if (action.manager_actif !== undefined) {
                // **L'interrupteur coupe l'exécution, pas seulement le chargement** (leçon de
                // `fil.ts:591`) : un état sérialisé qui porte encore le nœud ne rouvre pas l'étage.
                if (!managerActive(content) || !managerPossede(collectModifiers(state, vueEffets(content)))) {
                    throw new InvalidAction("mecanique_verrouillee", { mecanique: "manager" });
                }
                // Rappel EXPLICITE (hors suspension §6.3, qui est gratuite) : la prime du manager.
                if (action.manager_actif &&
                    state.flags.manager_actif === false &&
                    state.flags.manager_suspendu_avec_producteur !== true) {
                    const primeManager = primeRetourManager(state, content, action.manager_candidat_id);
                    if (state.money < primeManager) {
                        throw new InvalidAction("not_enough_money", {
                            requis: primeManager,
                            solde: Math.round(state.money),
                            raison: "prime_retour_manager",
                        });
                    }
                }
            }
            break;
        case "clore_run":
            // Les deux sorties d'après-million (PLAN §4.13) : elles n'existent qu'une fois la
            // victoire gravée, et `clore_run` ne se joue qu'une fois (le statut `ended` ferme la
            // porte au tour suivant, garde ci-dessus).
            if (!state.victory_locked)
                throw new InvalidAction("run_not_active");
            break;
        case "continuer_apres_rue":
            if (!state.victory_locked)
                throw new InvalidAction("run_not_active");
            // Continuer deux fois n'a aucun sens : le mode 100 % est déjà en cours.
            if (state.flags.apres_la_rue)
                throw new InvalidAction("run_not_active");
            break;
        default:
            break;
    }
    // ⟦A-29⟧ **« Ce qui coûte de l'argent est dans le marché. »** La règle est GÉNÉRALE, pas un
    // garde posé action par action : toute action dont le coût comporte des Clicks passe par
    // l'**étage 7 de l'escalier** ⟦A-5⟧, celui que le Grand Saut ouvre ⟦A-1⟧. Un garde écrit
    // action par action serait oublié par la prochaine qui coûtera des Clicks ; écrit ici, il est
    // acquis — y compris par les coûts qui ne viennent pas de la table (`mods`, production value).
    //
    // **Pourquoi ICI et pas plus haut.** C'est un verrou d'étage, donc il précède les ressources
    // (« tu n'as pas encore ça » vaut mieux que « tu es fauché », §2.2) — mais il vient APRÈS le
    // `switch`, qui porte des refus plus précis pour les actions qui en ont un : `respec_locked`,
    // `logement_invalide`, `production_value_invalide` disent au joueur ce qui lui manque
    // vraiment. Le passer avant les écrasait tous les trois d'un « pas encore d'argent »
    // générique — un test du serveur l'a signalé, et il avait raison.
    //
    // ⟦A-10⟧ **État neutre, et c'est démontrable** : avant le Grand Saut, `state.money` vaut 0 et
    // ne peut pas bouger — `effects.ts` ignore toute écriture sur la jauge `argent`, `estMonetise`
    // et `chargesDuJour` sont éteints. Un coût en Clicks y était donc DÉJÀ refusé, trois lignes
    // plus bas, en `not_enough_money`. On ne ferme rien de jouable : on refuse **mieux**.
    //
    // La seule action réellement concernée aujourd'hui est `therapie` (−150 ₵, aucun garde) : le
    // respec attend 10 000 abonnés, `reprendre_studio` exige d'être rentré chez ses parents (donc
    // d'en être parti, donc le Grand Saut), la production value attend 100 000, et les 17 objets
    // de boutique ont tous `palier_min ≥ 1 000`. Le recensement complet est dans ⟦A-29⟧.
    //
    // ⟦C60 bis · A-177⟧ **La thérapie reste un remède d'après le Grand Saut, même quand elle ne coûte plus un Click.**
    // Elle ne se paie plus qu'en temps et en énergie (`systemes.therapie_clicks` = 0), donc `money_delta` vaut 0 et la
    // ligne ci-dessous ne l'attraperait plus : sans cette clause, la thérapie s'ouvrirait dès la première heure de
    // l'arc 1, où ⟦A-29⟧ veut que le sommeil soit LE remède. Même disponibilité qu'avant, même refus.
    if ((cout.money_delta < 0 || action.type === "therapie") && !argentOuvert(state)) {
        throw new InvalidAction("mecanique_verrouillee", {
            mecanique: "argent",
            cout_clicks: -cout.money_delta,
            subscribers: state.subscribers,
        });
    }
    // Ressources : seules les actions qui en dépensent sont gardées.
    if (cout.energy_delta < 0 && state.energy < -cout.energy_delta) {
        throw new InvalidAction("not_enough_energy", {
            requis: -cout.energy_delta,
            disponible: state.energy,
        });
    }
    if (cout.money_delta < 0 && state.money < -cout.money_delta) {
        throw new InvalidAction("not_enough_money", {
            requis: -cout.money_delta,
            disponible: state.money,
        });
    }
}
/**
 * ⟦C62 · lot moteur · A-179⟧ Retient le pic d'abonnés de l'action (`HookContext.picAbonnes`). Idempotent,
 * sans effet sur l'état. Appelée aux seuls endroits de la file où les abonnés peuvent monter puis
 * redescendre dans la même action : à l'entrée (un état posé à pile 1 000 000), après chaque frontière
 * de jour (les vues), après la rente de la nuit (avant les désabonnés), après l'effet propre de l'action
 * et après le tirage d'événement.
 */
export function noterPicAbonnes(ctx) {
    const abonnes = ctx.state.subscribers;
    if (abonnes > (ctx.picAbonnes ?? 0))
        ctx.picAbonnes = abonnes;
}
function clamp(v, min, max) {
    return Math.min(max, Math.max(min, v));
}
/** §2.3 pas 9 — clamps de toutes les jauges bornées. */
export function appliquerClamps(state) {
    // ⟦A-20⟧ **Le seul endroit qui écrête l'énergie**, et il connaît la surcharge. Sans
    // surcharge accordée, `plafondEnergie` vaut 100 : l'état neutre, au point près.
    state.energy = clamp(state.energy, 0, plafondEnergie(state));
    state.mental = clamp(state.mental, 0, 100);
    state.reputation = clamp(state.reputation, 0, 100);
    state.loyalty = clamp(state.loyalty, 0, 100);
    // Plafond de momentum DYNAMIQUE : abaissé de 0,05 par pause annoncée au-delà de la 6e
    // (avenant A-2 point 5d — l'irrégularité chronique se paie structurellement).
    state.momentum = clamp(state.momentum, 0.8, plafondMomentum(state));
    state.subscribers = Math.max(0, state.subscribers);
    state.subs_volatile = clamp(state.subs_volatile, 0, state.subscribers);
    if (state.inspiration.length > MAIN_MAX)
        state.inspiration.length = MAIN_MAX;
    if (state.alignment_history.length > HISTORIQUE_ALIGNEMENT_MAX) {
        state.alignment_history = state.alignment_history.slice(-HISTORIQUE_ALIGNEMENT_MAX);
    }
}
/**
 * §7.1 — image de chaîne = moyenne arrondie des 10 derniers alignements, **plus l'offset**
 * (arbitrage **A33**) : la moyenne seule est purement dérivée, donc rien ne permettait aux
 * comportements hors publication (pause abusive, dons, excuses — avenants A-2 et A-4) de
 * déplacer l'image. `alignment_history` garde son sens exact ; l'offset porte le reste.
 */
export function recalculerImage(state) {
    const moyenne = state.alignment_history.length === 0
        ? 0
        : state.alignment_history.reduce((a, b) => a + b, 0) / state.alignment_history.length;
    state.channel_image = clamp(Math.round(moyenne + (state.channel_image_offset ?? 0)), -100, 100);
}
/**
 * ⟦C60 · A-177⟧ **L'image de chaîne n'existe plus** quand l'alignement est coupé : plus d'historique
 * d'alignements, plus de cicatrice (`channel_image_offset`), plus d'image. Appelée au pas 10 de la
 * file par `progressionEtFins` (`index.ts`), jamais quand l'alignement est actif, donc sans effet
 * sur le jeu d'avant. MUTE `state`.
 */
export function neutraliserImage(state) {
    if (state.alignment_history.length > 0)
        state.alignment_history = [];
    if (state.channel_image_offset !== undefined && state.channel_image_offset !== 0) {
        state.channel_image_offset = 0;
    }
    state.channel_image = 0;
}
/**
 * La file de résolution de SPEC-MOTEUR §2.3, dans son **ordre strict** — le déterminisme
 * en dépend. L'état d'entrée n'est jamais muté.
 */
export function applyAction(state, action, seed, content, hooks = {}) {
    // 0. ⟦A-11 §1⟧ Les modificateurs de coût, agrégés UNE fois, sur l'état d'AVANT l'action —
    //    c'est ce qui garantit que la validation et le débit parlent du même coût.
    const mods = hooks.coutMods?.(state, action, content);
    // 1. valider — sinon InvalidAction, AUCUN effet, turn inchangé. La graine est passée : sans
    //    elle, le marché des freelances (§2.2) ne saurait pas dire qui est disponible aujourd'hui.
    validateAction(state, action, content, mods, seed);
    const next = structuredClone(state);
    // ⟦A-16⟧ Le récapitulatif est ouvert AVANT le premier débit : il doit voir tout ce que
    // l'action fait, à commencer par « le travail » (`cout_action`), sans quoi le joueur ne
    // verrait qu'un solde et conclurait au bug.
    const recap = nouveauRecap(next);
    const ctx = {
        state: next,
        content,
        seed,
        events: [],
        log: [],
        crunch: false,
        recap,
    };
    // 2. turn += 1
    next.turn += 1;
    // ⟦C62 · A-179⟧ Le pic d'abonnés commence AU compte d'entrée : un état posé à pile 1 000 000 (le mode
    // triche, une sauvegarde chargée à cet instant) a déjà franchi le million, et la nuit qui suit ne
    // doit pas le lui reprendre.
    noterPicAbonnes(ctx);
    // 3. débiter les coûts ; crunch si energy < 20 sur une production.
    const cout = actionCost(action, next, mods, systemes(content));
    const estProduction = action.type === "produce_video" || action.type === "video_jalon";
    if (estProduction && next.energy < SEUIL_CRUNCH) {
        ctx.crunch = true;
        next.mental += CRUNCH_SM;
        recap.ligne("crunch", "sante_mentale", CRUNCH_SM);
    }
    next.energy += cout.energy_delta;
    next.money += cout.money_delta;
    next.mental += cout.mental_delta;
    // ⟦A-16⟧ « Le travail », ligne à ligne : ce sont les valeurs de la table §2.2 telles
    // qu'elles viennent d'être débitées, jamais un solde recalculé.
    recap.ligne("cout_action", "energie", cout.energy_delta);
    recap.ligne("cout_action", "argent", cout.money_delta);
    recap.ligne("cout_action", "sante_mentale", cout.mental_delta);
    if (cout.cards_consumed > 0) {
        const avantCartes = next.inspiration.length;
        defausserCartes(next, action, cout.cards_consumed);
        recap.ligne("cout_action", "inspiration", next.inspiration.length - avantCartes);
    }
    // 3bis. ⟦A-20⟧ **La grasse matinée.** Le compteur de nuits consécutives vit ici, et nulle
    //   part ailleurs : c'est la seule position d'où AUCUNE action ne peut oublier de le
    //   remettre à zéro — la remise à zéro est ce qui donne son sens à « consécutifs ».
    //   Accordé AVANT le clamp du pas 9, sinon le `+80 ⚡` de la nuit serait écrêté à 100 puis
    //   le plafond s'ouvrirait sur une jauge déjà pleine : le bonus n'existerait pas.
    next.counters.consecutive_sleeps =
        action.type === "sleep" ? (next.counters.consecutive_sleeps ?? 0) + 1 : 0;
    if (action.type === "sleep") {
        const ouverte = grasseMatinee(next);
        if (ouverte > 0) {
            ctx.events.push({
                kind: "warning",
                data: {
                    scripted: "grasse_matinee",
                    surcharge_ouverte: ouverte,
                    plafond_energie: plafondEnergie(next),
                    nuits_consecutives: next.counters.consecutive_sleeps,
                },
            });
            ctx.log.push({
                turn: next.turn,
                clock_h: next.clock_h,
                famille: "quotidien",
                text: `Deuxième nuit d'affilée : tu te réveilles en pleine forme (énergie jusqu'à ${plafondEnergie(next)}).`,
            });
        }
    }
    // 4. clock_h += heures(action)
    const avant = next.clock_h;
    // ⟦A-14 §2⟧ Le repos se calcule sur l'état d'AVANT l'avancée d'horloge : `effetRepos` a
    // besoin du `clock_h` de départ pour savoir depuis combien de temps le joueur est debout.
    const repos = effetRepos(next, action, cout.hours);
    next.clock_h += cout.hours;
    // 4bis. ⟦A-14 §2⟧ Dépassement des heures de la journée, et nuits contenues par une action
    //   longue. Appliqué APRÈS l'avancée d'horloge : `last_rest_h` doit porter l'instant où
    //   l'action se termine, pas celui où elle commence.
    appliquerRepos(next, repos);
    // ⟦A-16⟧ **Les deux lignes qui ont déclenché l'avenant.** Elles touchent la MÊME jauge dans
    // la même fonction : un instantané les fondrait en un solde, exactement ce qu'A-16 interdit
    // (« −10 le travail, +3 tes nuits », pas « −7 »). D'où `ligne`, avec les valeurs exactes.
    recap.ligne("depassement", "sante_mentale", -repos.cout_depassement);
    recap.ligne("nuits_contenues", "sante_mentale", repos.sm_nuits_contenues, String(repos.nuits_contenues));
    if (repos.cout_depassement > 0 || repos.sm_nuits_contenues > 0) {
        ctx.events.push({
            kind: "warning",
            data: {
                scripted: "repos",
                cout_depassement: repos.cout_depassement,
                sm_nuits_contenues: repos.sm_nuits_contenues,
                nuits_contenues: repos.nuits_contenues,
                heures_depassement: repos.depassement_apres,
                // « Prévenir, jamais surprendre » : c'est CE flag que l'UI attend. Il est vrai au
                // premier franchissement des 16 h, quand la note est encore de −1 ou −3 — donc
                // AVANT le mur, pas pendant. Le prix exact du coup suivant se prévisualise avec
                // `previsionRepos` (`repos.ts`), sans jouer l'action.
                premier_depassement: repos.depassement_avant === 0 && repos.depassement_apres > 0,
                alerte: repos.cout_depassement >= SM_DEPASSEMENT_ALERTE,
            },
        });
    }
    if (repos.cout_depassement > 0) {
        const debout = heuresDepuisRepos(next, avant + cout.hours);
        ctx.log.push({
            turn: next.turn,
            clock_h: next.clock_h,
            famille: "quotidien",
            text: repos.depassement_avant === 0
                ? `Debout depuis ${debout} h. La fatigue s'installe (−${repos.cout_depassement} de santé mentale) — et chaque heure de plus coûtera davantage.`
                : `Toujours debout, ${debout} h au compteur. −${repos.cout_depassement} de santé mentale.`,
        });
    }
    // 4ter. ⟦A-15 §1⟧ Les idées périmées quittent la main. Placé APRÈS l'avancée d'horloge et
    //   AVANT l'effet propre de l'action (pas 6) : les cartes que l'action vient de consommer
    //   sont déjà défaussées (pas 3), donc on ne périme jamais une idée qui vient de servir.
    // ⟦A-15 §4⟧ Les fenêtres échues se referment ici aussi : depuis ⟦A-14⟧ une evergreen dure
    //   64 h, et une collab proposée la veille doit pouvoir partir pendant qu'on tourne.
    for (const o of recap.mesurer("opportunite_expiree", () => purgerOpportunites(next, ctx.events, ctx.log))) {
        recap.auJour(jour(o.expires_h), () => recap.fait("opportunite_expiree", o.event_id));
    }
    const perimees = recap.mesurer("idee_perimee", () => purgerCartesPerimees(next, ctx.events, ctx.log));
    // Une ligne NARRATIVE par idée perdue : « ton idée *Console du grenier* s'est périmée ».
    // C'est la prévenance d'⟦A-15⟧ — un chiffre global ne dirait pas LAQUELLE on a laissé
    // filer. Datée du jour où l'idée est réellement tombée, pas de la fin de l'action.
    for (const c of perimees) {
        recap.auJour(jour(c.expires_h ?? next.clock_h), () => recap.fait("idee_perimee", c.subject_id));
    }
    // 4quater. ⟦A-15 §3⟧ **Le rival a monté pendant ce temps-là.** Observateur pur : il ne
    //   retire rien au joueur, il raconte. C'est la seule pression du lot qui n'ait aucune
    //   contrepartie mécanique — et c'est précisément ce qui lui donne sa force : on la REGARDE.
    //
    // ⚠️ **`fait`, surtout pas `ligne`.** C'est une ligne NARRATIVE, comme les idées périmées et
    //   les fenêtres échues : un PNJ ne prend pas un abonné au joueur, son delta est nul **par
    //   construction**. Or `recap.ligne` jette tout ce dont `|delta| < 1e-9` — la posant en
    //   `ligne("rival", "abonnes", 0, …)`, le rival d'⟦A-16⟧ était donc **systématiquement
    //   supprimé du récapitulatif**, depuis le premier jour (écart relevé par ⟦A-32⟧). Le seul
    //   écran qui explique au joueur ce que le temps lui coûte n'a jamais montré la pression que
    //   l'avenant appelle « la plus forte psychologiquement ».
    for (const r of rivaux(next)) {
        const gagnes = progressionRival(r, avant, next.clock_h);
        if (gagnes <= 0)
            continue;
        recap.fait("rival", `${rivalDuContenu(content, r.id)?.nom ?? r.id}:+${gagnes}`);
    }
    signalerProgressionRivaux(next, content, avant, next.clock_h, ctx.events);
    // 5. pour CHAQUE frontière de jour franchie, dans l'ordre.
    // ⟦A-16⟧ Chaque frontière est déjà résolue une à une : il suffit de ne plus les agréger.
    // Une longue de 48 h produit donc trois blocs de lignes, datés, dans l'ordre.
    const joursTraverses = frontieresDeJour(avant, next.clock_h);
    for (const jourAtteint of frontieresDeJour(avant, next.clock_h)) {
        // Chaque bloc est daté du jour qu'il résout, pas du `clock_h` d'arrivée — sans quoi les
        // quatre journées d'une evergreen s'écraseraient en une seule à l'écran.
        recap.auJour(jourAtteint, () => {
            recap.mesurer("vues_versees", () => hooks.frontiereDeJour?.(ctx, jourAtteint)); // a. (M1.10)
            // ⟦C62 · A-179⟧ Les vues de la frontière viennent de tomber : c'est ici que le million se franchit
            // le plus souvent, avant que la nuit ne reprenne ses désabonnés.
            noterPicAbonnes(ctx);
            // a-bis. ⟦§6.2 · A-81⟧ Les salaires, AVANT les charges — l'ordre qu'avait la file quand
            //   ils vivaient dans le hook précédent ; seule la CAUSE change, jamais l'opération.
            recap.mesurer("salaires", () => hooks.paieEquipe?.(ctx, jourAtteint));
            const charges = chargesDuJour(next, systemes(ctx.content));
            next.money -= charges; // b. charges fixes (0 avant le Grand Saut, avenant A-1)
            recap.ligne("charges", "argent", -charges);
            // b-bis. ⟦A-20⟧ La surcharge redescend — c'est le « temporaire » de l'avenant. Le
            //   clamp du pas 9 ramènera la jauge sous son nouveau plafond, et lui seul : deux
            //   endroits qui écrêtent la même jauge donnent un solde inexplicable à l'écran.
            //
            //   ⚠️ **Pas de ligne de récapitulatif**, à dessein et à regret. `RecapCause` est un
            //   enum fermé mappé par un `Record` EXHAUSTIF côté client ⟦A-16⟧ : y ajouter
            //   `surcharge_retombe` casse sa compilation tant que personne n'a décidé comment la
            //   cause se dit en français — c'est le garde-fou voulu, pas un accident. En attendant
            //   cette décision, l'événement ci-dessous porte l'information, et le silence est
            //   cohérent avec les autres clamps, qui n'ont jamais eu de ligne non plus.
            if (decroitreSurcharge(next) > 0 && surchargeAccordee(next) === 0) {
                ctx.events.push({
                    kind: "warning",
                    data: { scripted: "surcharge_retombee", plafond_energie: plafondEnergie(next) },
                });
            }
            if (next.channel_image <= -40) {
                next.reputation -= 1; // c. érosion chaos (PLAN §4.7)
                recap.ligne("erosion_chaos", "reputation", -1);
            }
            // c-bis. ⟦A-74⟧ **La récompense de la ligne lumière — SPEC-MOTEUR §5.1 / table §7.2 :
            //   « REP +1 par semaine si image ≥ +40 ».** Le pendant négatif (l'érosion ci-dessus)
            //   était écrit depuis le premier jour ; la moitié lumière ne l'a jamais été — la
            //   symétrie promise par la table §7.2 (« REP + lente ») restait un mensonge par
            //   omission. La cadence est HEBDOMADAIRE là où l'érosion est quotidienne : c'est la
            //   spec (« lente »), pas une prudence — le chaos coûte sept fois plus vite que la
            //   lumière ne paie. Le gain passe par le trait ⟦A-4 §2⟧ comme tout gain de REP
            //   (« le préjugé favorable » : resoudreExcuses fait pareil), et le clamp du pas 9
            //   bornera à 100. 🔴 Rupture d'état neutre ASSUMÉE et mesurée (⟦A-68⟧ : dite, jamais
            //   subie) — le md5 avant/après et les six cibles ⟦A-40⟧ sont dans l'avenant A-74.
            if (next.channel_image >= 40 && jourAtteint > 0 && jourAtteint % 7 === 0) {
                const gain = appliquerTraitJauge(next.genre, "reputation", 1);
                next.reputation += gain;
                recap.ligne("rep_lumiere", "reputation", gain);
            }
        });
    }
    // 6. effet propre de l'action.
    //    ⟦A-16⟧ `publier` et la résolution de nuit posent elles-mêmes leurs lignes fines
    //    (abonnés de pic, rente, désabonnements, momentum) : la mesure d'ensemble ne sert
    //    qu'à rattraper ce qu'aucune cause plus précise ne couvre.
    recap.mesurer(action.type === "produce_video" || action.type === "video_jalon"
        ? "publication"
        : action.type === "sleep"
            ? "nuit"
            : "effet_action", () => appliquerEffetAction(ctx, action, cout, hooks));
    // ⟦C62 · A-179⟧ Après l'effet propre de l'action (la nuit a rendu ses désabonnés, un choix d'événement
    // a joué ses abonnés) : le pic retient aussi ce que l'action a fait monter.
    noterPicAbonnes(ctx);
    // 6bis. ⟦SPEC-FIL §3.1⟧ **Le Fil se remplit.** Entre l'effet de l'action et le tirage
    //   d'événement, aux deux mêmes moments que lui — voir `ResolutionHooks.filArrivee` pour
    //   pourquoi cet interstice précis et pas un autre.
    //
    //   ⚠️ **Hors `recap.mesurer`**, à dessein : `RecapCause` est un enum fermé mappé par un
    //   `Record` EXHAUSTIF côté client ⟦A-16⟧, et la cause `fil` appartient à M3.23 avec l'écran
    //   qui la dira en français. Mesurer l'arrivée sous une cause voisine ferait apparaître des
    //   lignes « effet de l'action » que rien n'expliquerait. Une arrivée ne touche d'ailleurs
    //   aucune jauge : elle n'a rien à récapituler tant que le joueur ne la voit pas.
    if (action.type === "produce_video" || action.type === "video_jalon") {
        hooks.filArrivee?.(ctx, "publication");
    }
    else if (action.type === "sleep") {
        hooks.filArrivee?.(ctx, "sommeil");
    }
    // 7-8. un seul tirage d'événement, à la publication ou au sommeil.
    recap.mesurer("evenement", () => {
        if (action.type === "produce_video" || action.type === "video_jalon") {
            hooks.tirerEvenement?.(ctx, "publication");
        }
        else if (action.type === "sleep") {
            hooks.tirerEvenement?.(ctx, "sommeil");
        }
    });
    // ⟦C62 · A-179⟧ Un événement-notification applique ses effets (abonnés compris) sur-le-champ.
    noterPicAbonnes(ctx);
    // 9. clamps.
    appliquerClamps(next);
    // 10. image, niveaux, paliers, victoire, game over.
    recalculerImage(next);
    recap.mesurer("progression", () => hooks.progression?.(ctx));
    appliquerClamps(next);
    return {
        state: next,
        events: ctx.events,
        log: ctx.log,
        // ⟦A-16⟧ La décomposition remonte jusqu'ici. `jours_traverses = 0` ⇒ l'UI n'ouvre pas de
        // panneau : une action qui ne franchit aucune frontière n'a rien à raconter.
        recap: recap.rendre(avant, joursTraverses.length),
    };
}
/**
 * ⟦SPEC-FIL §4-§5 · A-16⟧ Ce que le joueur lit dans le journal après avoir traité un lot.
 *
 * Le moteur n'écrit **aucun texte de commentaire** (CLAUDE.md : contenu = données) : il ne
 * raconte que ce qu'il a fait — combien de messages, combien de vannes passées, ce qu'un
 * cadeau a donné. Les mots des commentaires eux-mêmes se relisent dans `content/comments.json`
 * à l'affichage, ce qui rend une correction de contenu rétroactive.
 *
 * ⚠️ **Une vanne ratée doit se voir**, même noyée dans un lot réussi : c'est la seule réponse
 * qui coûte quelque chose qu'on n'avait pas prévu, et ⟦A-16⟧ interdit qu'un solde tienne lieu
 * d'explication.
 */
function journaliserLot(ctx, rapport, cartes) {
    const { state } = ctx;
    const ligne = (text) => {
        ctx.log.push({ turn: state.turn, clock_h: state.clock_h, famille: "quotidien", text });
    };
    if (rapport.traites.length === 0) {
        // Régime « legacy » ou « Fil non traité » ⟦§6.3⟧ : le texte d'avant le Fil, mot pour mot.
        ligne(state.channel_image <= -40
            ? "Les commentaires piquent. Ça coûte double."
            : `Lecture des commentaires${cartes.piochees > 0 ? " — une idée en sort" : ""}.`);
        return;
    }
    const n = rapport.traites.length;
    ligne(`${n} commentaire${n > 1 ? "s" : ""} traité${n > 1 ? "s" : ""}.`);
    const vannes = rapport.traites.filter((t) => t.humour !== null);
    const passees = vannes.filter((t) => t.humour === true).length;
    if (vannes.length > 0) {
        ligne(passees === vannes.length
            ? `La vanne passe${vannes.length > 1 ? "nt" : ""}.`
            : passees === 0
                ? "Tu t'es moqué d'un fan. Ça ne passe pas."
                : `${passees} vanne${passees > 1 ? "s" : ""} sur ${vannes.length} passe${passees > 1 ? "nt" : ""}.`);
    }
    if (state.flags.clip_viral_fil === true) {
        ligne("Ta réponse tourne en clip. La prochaine vidéo part avec un peu d'avance.");
    }
    if (rapport.hook !== null)
        ligne("Ce commentaire-là va avoir des suites.");
    for (const t of rapport.traites) {
        if (t.cadeau_perdu === "hook_deja_arme") {
            ligne("Deux pistes d'un coup — tu n'en suivras qu'une.");
        }
    }
    if (cartes.cadeauxRecus > 0) {
        ligne(cartes.cadeauxRecus > 1
            ? `${cartes.cadeauxRecus} idées sorties des commentaires.`
            : "Une idée sortie des commentaires.");
    }
    if (cartes.cadeauxPerdus > 0) {
        ligne("Une idée dans les commentaires, et la main pleine. Tant pis pour celle-là.");
    }
    if (rapport.censure_posee) {
        ligne("Tu supprimes beaucoup. Ça commence à se remarquer.");
    }
}
/**
 * Défausse les cartes consommées : le sujet principal d'abord, puis les
 * `extra_card_subjects` désignés (§2.2, choix §14.6).
 */
function defausserCartes(state, action, combien) {
    const aRetirer = [];
    if (action.type === "produce_video") {
        aRetirer.push(action.subject_id);
        if (action.format === "longue") {
            for (const id of action.extra_card_subjects ?? []) {
                if (id !== action.subject_id && !aRetirer.includes(id))
                    aRetirer.push(id);
            }
        }
    }
    for (const id of aRetirer.slice(0, combien)) {
        const i = state.inspiration.findIndex((c) => c.subject_id === id);
        if (i >= 0)
            state.inspiration.splice(i, 1);
    }
    // Filet : un remaster consomme « 1 carte » sans en désigner une (§2.2) — la plus ancienne.
    let reste = combien - Math.min(combien, aRetirer.length);
    while (reste > 0 && state.inspiration.length > 0) {
        state.inspiration.shift();
        reste--;
    }
}
function appliquerEffetAction(ctx, action, cout, hooks) {
    const { state } = ctx;
    // Les actions dont l'effet dépend du contenu passent par leur hook dédié ; si le hook a
    // traité l'action, on n'y touche pas ici.
    if (hooks.effetAction?.(ctx, action))
        return;
    switch (action.type) {
        case "produce_video":
            hooks.publier?.(ctx, action);
            break;
        case "sleep": {
            const deltaH = state.clock_h - state.counters.last_sleep_h;
            state.nights += 1;
            // Le reste de la nuit (désabos, rente, momentum, tendances, strikes) est en M1.11 ;
            // ce squelette pose seulement ce qui ne dépend de rien d'autre.
            hooks.nuit?.(ctx, deltaH);
            state.counters.subs_at_last_sleep = state.subscribers;
            state.counters.last_sleep_h = state.clock_h;
            // ⟦A-14 §2⟧ Une vraie nuit est aussi un repos : les deux compteurs se rejoignent ici,
            // et ici seulement. Ailleurs `last_rest_h` avance seul (nuits contenues, `repos.ts`).
            state.counters.last_rest_h = state.clock_h;
            ctx.log.push({
                turn: state.turn,
                clock_h: state.clock_h,
                famille: "quotidien",
                text: `Nuit ${state.nights} — énergie remise à ${Math.min(100, state.energy)}.`,
            });
            break;
        }
        case "veille": {
            const piochees = piocherCartes(state, ctx.content, ctx.seed, cout.cards_drawn);
            ctx.log.push({
                turn: state.turn,
                clock_h: state.clock_h,
                famille: "quotidien",
                text: piochees > 0
                    ? `Veille : ${piochees} nouvelle${piochees > 1 ? "s" : ""} idée${piochees > 1 ? "s" : ""} en main.`
                    : "Veille : rien de neuf, la main est pleine.",
            });
            break;
        }
        case "lire_commentaires": {
            // ⟦SPEC-FIL §6.1⟧ **La carte de base reste**, et ce n'est pas un oubli : les cadeaux du
            // Fil s'y AJOUTENT. Sans elle, lire le Fil serait parfois moins rentable que de ne pas
            // le lire — ce qui violerait la propriété n° 2 du §0 (« le Fil ne peut jamais être pire
            // que son absence »). C'est la question ouverte n° 4 du §13, défaut retenu : les deux.
            const piochees = piocherCartes(state, ctx.content, ctx.seed, cout.cards_drawn);
            // ⟦SPEC-FIL §4-§5⟧ Le lot, s'il y en a un. `traiterFil` sort avant tout effet quand le
            // Fil est éteint : sans charge utile, tout ce bloc est un `rapport` vide et l'action se
            // comporte exactement comme avant ⟦A-10⟧.
            const rapport = traiterFil(state, ctx.content, ctx.seed, action.traitements ?? []);
            // ⟦§5.1⟧ Les cadeaux `idee`. La main pleine **perd** le cadeau, et le dit : une ligne de
            // journal plutôt qu'un silence — c'est la prévenance d'⟦A-15/A-16⟧, pas un détail.
            let cadeauxRecus = 0;
            let cadeauxPerdus = 0;
            for (const index of rapport.cartes) {
                if (piocherCartes(state, ctx.content, ctx.seed, 1, index) > 0)
                    cadeauxRecus++;
                else
                    cadeauxPerdus++;
            }
            journaliserLot(ctx, rapport, { piochees, cadeauxRecus, cadeauxPerdus });
            // ⟦SPEC-FIL §9.4 · A-82⟧ **Une ligne par réponse traitée** — `fil` · jauge · delta ·
            // détail = id du commentaire (le client le traduit en auteur). Elles étaient absentes :
            // `traiterFil` tourne sous `recap.mesurer("effet_action")`, et les deltas d'un lot
            // tombaient en bloc sous « l'effet de l'action », sans nom. Depuis A-82 §0, la mesure
            // d'ensemble déduit ce que ces lignes expliquent : aucun doublon.
            for (const t of rapport.traites) {
                ctx.recap?.ligne("fil", "fidelite", t.fidelite, t.comment_id);
                ctx.recap?.ligne("fil", "hype", t.hype, t.comment_id);
                ctx.recap?.ligne("fil", "sante_mentale", t.sm, t.comment_id);
                if (t.cadeau_perdu === "hook_deja_arme") {
                    ctx.recap?.fait("fil", "Deux pistes d'un coup — tu n'en suivras qu'une.");
                }
                // ⟦§4.3 · §9.3⟧ Ce que l'écran a besoin de savoir pour afficher la réplique écrite
                // pour CE commentaire (`reponse_humour_reussie` / `_ratee`, contenu) : l'issue de la
                // vanne. Le moteur ne porte que l'id et le verdict — jamais le texte.
                ctx.events.push({
                    kind: "fil_reponse",
                    data: { uid: t.uid, comment_id: t.comment_id, reponse: t.reponse, humour: t.humour },
                });
            }
            if (cadeauxPerdus > 0) {
                ctx.recap?.fait("fil", cadeauxPerdus > 1
                    ? `${cadeauxPerdus} idées dans les commentaires, et la main pleine. Tant pis pour celles-là.`
                    : "Une idée dans les commentaires, et la main pleine. Tant pis pour celle-là.");
            }
            break;
        }
        case "regler_cm": {
            // ⟦SPEC-FIL §9.5 · A-82⟧ La bascule, et rien d'autre : la nuit (`nuitCM`) relit le drapeau.
            const avant = state.flags.cm_actif !== false;
            state.flags.cm_actif = action.actif;
            if (avant !== action.actif) {
                ctx.log.push({
                    turn: state.turn,
                    clock_h: state.clock_h,
                    text: action.actif
                        ? "Ton community manager reprend le Fil : la routine, la nuit, à ta place."
                        : "Tu reprends la main sur le Fil. Ton community manager range son clavier — plus de salaire dès ce soir.",
                });
            }
            break;
        }
        case "menage": {
            const v = state.videos.find((x) => x.id === action.video_id);
            if (v) {
                v.visibility = "prive";
                // ⟦A-4 §2 / A-26⟧ Un gain de Fidélité du moteur : il passe par le trait comme les
                // `gauge_delta` du contenu. Sans ça, la créatrice payait son coût et n'encaissait rien.
                state.loyalty += appliquerTraitJauge(state.genre, "loyalty", 2);
                ctx.log.push({
                    turn: state.turn,
                    clock_h: state.clock_h,
                    famille: "chaine",
                    text: "Une nulle passée en privé. Personne n'a rien vu.",
                });
            }
            break;
        }
        // ⟦A-11 §2⟧ Le déménagement, dans les deux sens. Il n'y a **qu'un seul drapeau** :
        // `chez_les_parents` coupe le loyer (`chargesDuJour`) ET rabat le plafond de compétence
        // (`plafondCompetence`, `progress.ts`). Une seule vérité, deux lectures — comme
        // `grand_saut_done` pour la couche argent (⟦A-1⟧).
        case "retour_parents": {
            state.flags.chez_les_parents = true;
            ctx.events.push({
                kind: "warning",
                data: {
                    scripted: "retour_parents",
                    charges_par_jour: 0,
                    plafond_studio: false,
                },
            });
            ctx.log.push({
                turn: state.turn,
                clock_h: state.clock_h,
                famille: "argent",
                text: "Tu remontes les cartons dans ta chambre d'ado. Plus de loyer — et plus de studio.",
            });
            break;
        }
        case "reprendre_studio": {
            state.flags.chez_les_parents = false;
            ctx.events.push({
                kind: "warning",
                data: {
                    scripted: "reprendre_studio",
                    charges_par_jour: CHARGES_FIXES_PAR_JOUR,
                    cout: COUT_REPRENDRE_STUDIO,
                },
            });
            ctx.log.push({
                turn: state.turn,
                clock_h: state.clock_h,
                famille: "argent",
                text: `Les clés, la caution, le camion : ${COUT_REPRENDRE_STUDIO} ₵. Tu retrouves un vrai studio.`,
            });
            break;
        }
        // Les actions restantes (remaster, ranger_serie, acheter_skill, acheter_objet, respec,
        // pause, therapie, choix_evenement, clore_run, continuer_apres_rue, video_jalon) sont
        // portées par leur module : sans leur hook, elles n'appliquent que leur coût.
        default:
            break;
    }
}
//# sourceMappingURL=actions.js.map